import React, { useState, useMemo, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "react-feather";
import "./DatePicker.scss";

interface DatePickerProps {
  onDateChange?: (date: Date | null) => void;
  initialDate?: Date;
}

// Moved outside component to prevent recreation
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function DatePicker(props: DatePickerProps) {
  const [currentMonth, setCurrentMonth] = useState(
    () => props.initialDate || new Date()
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    () => props.initialDate || null
  );

  // Utility functions moved to custom hook for better organization
  const { formatMonth, getDaysInMonth, isToday } = useDateUtils();

  // Memoized handlers
  const handlePrevMonth = useCallback(() => {
    setCurrentMonth(
      (prevMonth) => new Date(prevMonth.getFullYear(), prevMonth.getMonth() - 1)
    );
  }, []);

  const handleNextMonth = useCallback(() => {
    setCurrentMonth(
      (prevMonth) => new Date(prevMonth.getFullYear(), prevMonth.getMonth() + 1)
    );
  }, []);

  const handleDateClick = useCallback(
    (date: Date) => {
      setSelectedDate(date);
      props.onDateChange?.(date);
    },
    [props.onDateChange]
  );

  // Memoized values
  const days = useMemo(
    () => getDaysInMonth(currentMonth),
    [currentMonth, getDaysInMonth]
  );
  const formattedMonth = useMemo(
    () => formatMonth(currentMonth),
    [currentMonth, formatMonth]
  );

  const weekDays = useMemo(
    () =>
      WEEKDAYS.map((day, index) => (
        <div key={index} className="weekday-cell">
          {day.substring(0, 2)}
        </div>
      )),
    []
  );

  // Memoize cell class computation
  const getCellClassName = useCallback(
    (date: Date | null, selectedDate: Date | null) => {
      if (!date) return "calendar-cell";

      const classNames = ["calendar-cell", "has-date"];

      if (selectedDate && date.getTime() === selectedDate.getTime()) {
        classNames.push("selected-date");
      }

      if (isToday(date)) {
        classNames.push("today");
      }

      return classNames.join(" ");
    },
    [isToday]
  );

  // Memoize calendar cells
  const calendarCells = useMemo(
    () =>
      days.map((date, index) => (
        <div
          key={index}
          className={getCellClassName(date, selectedDate)}
          onClick={() => date && handleDateClick(date)}
        >
          <span>{date ? date.getDate() : "\u00A0"}</span>
        </div>
      )),
    [days, selectedDate, getCellClassName, handleDateClick]
  );

  return (
    <div className="date-picker">
      <div className="month-header">
        <button
          className="nav-button prev"
          onClick={handlePrevMonth}
          aria-label="Previous month"
        >
          <ChevronLeft size={24} strokeWidth={1.67} />
        </button>
        <span className="month-text">{formattedMonth}</span>
        <button
          className="nav-button next"
          onClick={handleNextMonth}
          aria-label="Next month"
        >
          <ChevronRight size={24} strokeWidth={1.67} />
        </button>
      </div>
      <div className="calendar-grid">
        <div className="weekdays">{weekDays}</div>
        <div className="days">{calendarCells}</div>
      </div>
    </div>
  );
}

// Custom hook for date utilities
const useDateUtils = () => {
  const formatMonth = useCallback((date: Date) => {
    const month = date.toLocaleString("default", { month: "long" });
    const year = date.getFullYear();
    return `${month} ${year}`;
  }, []);

  const getDaysInMonth = useCallback((date: Date): (Date | null)[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();

    const days: (Date | null)[] = Array(firstDayOfMonth).fill(null);

    // Add the days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  }, []);

  const isToday = useCallback((date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }, []);

  return {
    formatMonth,
    getDaysInMonth,
    isToday,
  };
};
